import React, { useEffect } from 'react';
import { actions } from '@ecosystem/newron-design-system';
import './Modal.css';

const { IconButton } = actions;

const ModalContext = React.createContext<{
  onClose: () => void;
}>(null);

function useModalContext() {
  const context = React.useContext(ModalContext);

  if (!context) {
    throw new Error('Modal components cannot be rendered outside of the Modal component');
  }

  return context;
}

type HeaderProps = {
  children: React.ReactNode;
};

function Header({ children }: HeaderProps) {
  const { onClose } = useModalContext();

  return (
    <header className="flex items-center justify-between px-6 py-4 border-b border-default rounded-t">
      <span className="text-xl">{children}</span>
      {onClose && (
        <IconButton testId="modal-dismiss-btn" onClick={onClose}>
          <IconButton.icons.Close />
        </IconButton>
      )}
    </header>
  );
}

type BodyProps = {
  children: React.ReactNode;
  overflow?: boolean;
};

function Body({ children, overflow = true }: BodyProps) {
  return <main className={`relative px-6 pt-4 pb-6 flex-grow-0 ${overflow ? 'overflow-auto' : ''}`}>{children}</main>;
}

type FooterProps = {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'justify';
};

function Footer({ children, align = 'right' }: FooterProps) {
  return (
    <footer
      className={`flex items-center px-6 py-4 border-t border-default rounded-b ${
        align === 'right' ? 'justify-end' : align === 'justify' ? 'justify-between' : 'justify-start'
      }`}
    >
      {children}
    </footer>
  );
}

type Props = {
  children: React.ReactNode;
  onClose: () => void;
  isOpen: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
};

function Modal({ children, onClose, isOpen = false, size = 'md' }: Props) {
  const modalSizes = {
    sm: 'w-100',
    md: 'w-150',
    lg: 'w-200',
    xl: 'w-242',
    full: 'w-full',
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className="modal-wrapper flex items-center justify-center right-0 top-0 left-0 bottom-0 fixed px-12 py-0">
        <div className={`flex-column bg-white rounded-2 max-h-11/12 ${modalSizes[size]}`}>
          <ModalContext.Provider value={{ onClose }}>{children}</ModalContext.Provider>
        </div>
      </div>
      <div className="modal-backdrop fixed top-0 left-0 w-screen h-screen" />
    </>
  );
}

Modal.Header = Header;
Modal.Body = Body;
Modal.Footer = Footer;

export { Modal };
